import Map, { Marker, FullscreenControl } from 'react-map-gl';
import { useCallback, useEffect, useState } from 'react';
import { Avatar, Button, IconButton } from '@mui/material';
import { userService, pinsService, geocodingService, apiService } from '../../services';
import { ExtendedProfileData, PinnerProData } from '../../interfaces/Pinners';
import { PinIcon, PinPoco, PinAbb, PinPro, PinnerProIcon } from '../../assets/icons/Index';
import StylesControl from '../../components/specific/map/StylesControl';
import { MapStyle } from '../../types/map';
import InfoMarkerModal from '../../components/specific/modal/InfoMarkerModal';
import { MarkerData } from '../../types/markerData';
import { useTranslation } from 'react-i18next';
import DefaultMarkerModal from '../../components/specific/modal/DefaultMarkerModal';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { AppRoutes } from '../../enums/Approutes';
import { useParams, useNavigate } from 'react-router-dom';
import BioModal from '../../components/specific/modal/BioModal';

const Profile = () => {
  const { t } = useTranslation();
  const { userId } = useParams();
  const navigate = useNavigate();

  // raggruppati gli stati per il profilo utente per ottimizzare il codice
  const [userProfile, setUserProfile] = useState<ExtendedProfileData>({
    first_name: '',
    profile_picture: '',
    email: '',
    bio: '',
    id: '',
    project_name: '',
    is_solo_project: false,
    is_pinner_pro: false,
    public_residence: false,
    public_phone_number: false,
    phone_number: undefined,
    residence: '',
  });

  // Stati per la mappa
  const [mapConfig, setMapConfig] = useState({
    style: "mapbox://styles/mapbox/light-v9" as string,
    markers: [] as MarkerData[],
    popupInfo: null as MarkerData | null,
    residenceCoordinates: null as { longitude: number; latitude: number } | null,
    initialViewState: {
      longitude: 12.646361,
      latitude: 42.504154,
      zoom: 4,
    },
  });

  // Stati per i modali
  const [modals, setModals] = useState({
    isModalOpen: false,
    isDefaultMarkerModalOpen: false,
    isBioModalOpen: false,
  });

  const [isAuth, setIsAuth] = useState<boolean>(false);

  const getAuthTokenFromRefresh = async (refreshToken: string) => {
    try {
      const response = await apiService.post('/users/token_refresh/', {
        refresh: refreshToken
      });
      return response.data;
    } catch (error) {
      console.error('Error finding refreshToken', error);
      return null;
    }
  };

  useEffect(() => {
    const authToken = sessionStorage.getItem('authToken');
    const refreshToken = localStorage.getItem('refreshToken');

    if (authToken) {
      setIsAuth(true);
      return;
    }

    if (refreshToken) {
      getAuthTokenFromRefresh(refreshToken).then((data) => {
        if (data.access) {
          sessionStorage.setItem('authToken', data.access);
          setIsAuth(true);
        }
      });
    } else {
      setIsAuth(false);
    }
  }, []);


  const fetchMarkers = useCallback(async () => {
    if (!userId) return;
    try {
      const markersData = await pinsService.getUserMarkers(userId);
      setMapConfig(prev => ({ ...prev, markers: markersData }));
    } catch (error) {
      console.error('Error fetching markers:', error);
    }
  }, [userId]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!userId) return;
      try {
        const userData = await userService.getUserProfile(userId);

        // Aggiorno lo stato principale del profilo con i dati del Pinner semplice
        const userDataExtended = userData as PinnerProData & {
          public_residence?: boolean;
          public_phone_number?: boolean;
          is_pinner_pro?: boolean;
        };

        setUserProfile({
          id: userData.id,
          first_name: userData.first_name || '',
          profile_picture: userData.profile_picture || '/placeholder.svg',
          email: userData.email || '',
          bio: userData.bio || '',
          project_name: userData.project_name || '',
          is_solo_project: userData.is_solo_project ?? false,
          is_pinner_pro: userDataExtended.is_pinner_pro ?? userDataExtended.is_pinner_pro_pro ?? false,
          public_residence: userDataExtended.public_residence ?? false,
          public_phone_number: userDataExtended.public_phone_number ?? false,
          phone_number: userData.phone_number,
          residence: userData.residence || '',
        });

        // Gestisco le coordinate della residenza se pubblica
        if (userData.residence && userDataExtended.public_residence) {
          try {
            const coords = await geocodingService.getEnglishCoordinates(userData.residence);
            if (coords && coords.coordinates) {
              const [longitude, latitude] = coords.coordinates;
              setMapConfig(prev => ({
                ...prev,
                residenceCoordinates: { longitude, latitude },
                initialViewState: {
                  longitude,
                  latitude,
                  zoom: 4,
                },
              }));
            }
          } catch (error) {
            console.error('Errore nella geocodifica della residenza:', error);
          }
        }
      } catch (error) {
        console.error('Errore nel recupero dei dati utente:', error);
      }
    };
    fetchMarkers();
    fetchUserInfo();

    const handleProfilePictureUpdated = () => {
      fetchUserInfo();
    };

    window.addEventListener('profilePictureUpdated', handleProfilePictureUpdated);

    return () => {
      window.removeEventListener('profilePictureUpdated', handleProfilePictureUpdated);
    };
  }, [fetchMarkers, userId]);

  const handleSubscribeClick = () => {
    if (isAuth) {
      navigate(AppRoutes.PIN_MAP);
    } else {
      window.location.href = "https://www.pinnalo.it";
    }
  };

  const handleStyleChange = useCallback((style: MapStyle) => {
    setMapConfig(prev => ({ ...prev, style: style.styleUrl }));
  }, []);

  const handleMarkerClick = useCallback((marker: MarkerData) => {
    setMapConfig(prev => ({ ...prev, popupInfo: marker }));
    setModals(prev => ({ ...prev, isModalOpen: true }));
  }, []);

  const handleCloseModal = () => {
    setModals(prev => ({ ...prev, isModalOpen: false }));
    setMapConfig(prev => ({ ...prev, popupInfo: null }));
  };

  const handleResidenceMarkerClick = () => {
    setModals(prev => ({ ...prev, isDefaultMarkerModalOpen: true }));
  };

  const emailSubject = t('profile:email_subject');
  const emailBody = t('profile:email_body');
  const handleEmail = `https://mail.google.com/mail/?view=cm&fs=1&to=${userProfile.email || ''}&su=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;

  const getPinComponent = (option: number) => {
    switch (option) {
      case 0:
        return PinPoco;
      case 1:
        return PinAbb;
      case 2:
        return PinIcon;
      case 3:
        return PinPro;
      default:
        return PinPoco;
    }
  };

  // Handle Pinner Pro Heading message
  const handlePinnerProHeading = (user: ExtendedProfileData) => {
    const noProject = user.project_name === "";

    if (noProject) {
      return {
        leftKey: 'profile:hi_left',
        name: user.first_name,
        rightKey: 'profile:hi_right',
        map_description: 'profile:map_description'
      };
    }

    if (user.is_solo_project === true) {
      return {
        leftKey: 'profile:hi_left',
        name: user.project_name!,
        rightKey: 'profile:hi_right',
        map_description: 'profile:map_description'
      };
    }

    if (user.is_solo_project === false) {
      return {
        leftKey: 'profile:hi_project_left',
        name: user.project_name!,
        rightKey: 'profile:hi_project_right',
        map_description: 'profile:map_project_description'
      };
    };
  };

  return (
    <div className='w-full h-lvh flex flex-col gap-4 p-4 md:p-6'>
      <div className='w-full flex items-center flex-wrap justify-between gap-4 md:flex-row md:items-center md:justify-between'>
        {/* Sezione sinistra (Avatar e Titoli su desktop, solo Avatar su mobile) */}
        <div className='flex items-center gap-5'>
          {/* Avatar */}
          <div style={{ position: 'relative' }}>
            <Avatar
              onClick={() => setModals(prev => ({ ...prev, isBioModalOpen: true }))}
              alt={userProfile.first_name}
              src={userProfile.profile_picture || 'placeholder.svg'}
              sx={{ width: 60, height: 60, cursor: 'pointer' }}
            />
            {userProfile.is_pinner_pro && (
              <div style={{ position: 'absolute', bottom: -3, right: -5, zIndex: 10 }}>
                <PinnerProIcon className='' width={23} height={23} />
              </div>
            )}
          </div>

          {/* Titoli (visibili solo su desktop) */}
          {(() => {
            const headingMessage = handlePinnerProHeading(userProfile);
            if (!headingMessage) return null;

            return (
              <div className='hidden md:block'>
                <h1 className='mb-0 text-xl'>
                  {t(headingMessage.leftKey)} <strong className='text-primary cursor-pointer' onClick={() => setModals(prev => ({ ...prev, isBioModalOpen: true }))}>{headingMessage.name}</strong> {t(headingMessage.rightKey)}
                </h1>
                <h2 className='text-text-primary'>{t(headingMessage.map_description)}</h2>
              </div>
            );
          })()}
        </div>

        {/* Sezione destra (Bottoni) */}
        <div className='flex items-center gap-6 md:mt-0 md:gap-8'>
          {/* Pulsante Telefono */}
          {userProfile.public_phone_number && userProfile.phone_number && (
            <IconButton
              component='a'
              href={`https://wa.me/39${userProfile.phone_number}`}
              target='_blank'
              className='text-white'
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: '#E9495A',
                boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                '&:hover': {
                  backgroundColor: '#E6263B',
                }
              }}
            >
              <WhatsAppIcon />
            </IconButton>
          )}

          {/* Pulsante Email */}
          <IconButton
            component='a'
            href={handleEmail}
            target='_blank'
            className='text-white'
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: '#E9495A',
              boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
              '&:hover': {
                backgroundColor: '#E6263B',
              }
            }}
          >
            <EmailIcon />
          </IconButton>
        </div>

        {/* Titoli (visibili solo su mobile) */}
        <div className='block md:hidden w-full text-left text-sm'>
          {(() => {
            const headingMessage = handlePinnerProHeading(userProfile);
            if (!headingMessage) return null;
            return (
              <div className='block md:hidden w-full text-left text-sm'>
                <h1>{t(headingMessage.leftKey)} <strong className='text-primary'>{headingMessage.name}</strong> {t(headingMessage.rightKey)}</h1>
                <h2 className='text-text-primary'>{t(headingMessage.map_description)}</h2>
              </div>
            );
          })()}
        </div>
      </div>

      <Map
        initialViewState={mapConfig.initialViewState}
        style={{ flex: 1, borderRadius: '1rem', height: '100%' }}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
        mapStyle={mapConfig.style}
      >
        <FullscreenControl position='top-right' />
        <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 1 }}>
          <StylesControl onChange={handleStyleChange} />
        </div>

        {mapConfig.markers.map((marker, index) => {
          const Icon = getPinComponent(marker.knowledge_level);

          return (
            <Marker
              key={index}
              longitude={marker.longitude}
              latitude={marker.latitude}
              onClick={() => handleMarkerClick(marker)}
            >
              <Icon className='' />
            </Marker>
          )
        })}

        {mapConfig.residenceCoordinates && userProfile.public_residence && (
          <Marker
            longitude={mapConfig.residenceCoordinates.longitude}
            latitude={mapConfig.residenceCoordinates.latitude}
            onClick={(e) => {
              e.originalEvent.stopPropagation();
              handleResidenceMarkerClick();
            }}
          >
            <PinPro className="" />
          </Marker>
        )}
      </Map>

      <div className='w-full flex items-center justify-center mt-2'>
        {/* Subscribe Button */}
        <Button onClick={handleSubscribeClick} variant='contained' className='bg-primary hover:bg-primary-hover w-full md:w-80 md:mt-0'>
          {isAuth ? t('profile:your_map') : t('profile:create_map')}
        </Button>
      </div>

      {
        modals.isDefaultMarkerModalOpen && (
          <DefaultMarkerModal
            open={modals.isDefaultMarkerModalOpen}
            onClose={() => setModals(prev => ({ ...prev, isDefaultMarkerModalOpen: false }))}
            place={userProfile.residence || ''}
            text={t('pinMap:profile_marker_message')}
          />
        )
      }

      {
        mapConfig.popupInfo && (
          <InfoMarkerModal
            marker={mapConfig.popupInfo}
            open={modals.isModalOpen}
            onClose={handleCloseModal}
            hidePrivateContent={true}
          />
        )
      }

      {
        modals.isBioModalOpen && (
          <BioModal
            open={modals.isBioModalOpen}
            onClose={() => setModals(prev => ({ ...prev, isBioModalOpen: false }))}
            userId={userId!}
            isPinnerPro={userProfile.is_pinner_pro ?? false}
          />
        )
      }

    </div >
  );
};

export default Profile;